import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileSearch, MessageSquare, ShieldCheck, ClipboardList, Wallet, BarChart3, Check } from "lucide-react";

export default function Onboarding() {
  const { t, lang, setLang } = useI18n();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [states, setStates] = useState([]);
  const [form, setForm] = useState({ state: user?.state || "", zip_code: user?.zip_code || "", household_type: "" });

  useEffect(() => { api.get("/states").then((r) => setStates(r.data)); }, []);

  const goals = [
    { icon: FileSearch, labelKey: "goal_understand", to: "/documents" },
    { icon: MessageSquare, labelKey: "goal_ask", to: "/chat" },
    { icon: ShieldCheck, labelKey: "goal_protection", to: "/protection" },
    { icon: ClipboardList, labelKey: "goal_claim", to: "/claims" },
    { icon: Wallet, labelKey: "goal_organize", to: "/wallet" },
    { icon: BarChart3, labelKey: "goal_budget", to: "/budget" },
  ];

  const finish = async (dest) => {
    try {
      const res = await api.put("/profile", { ...form, language: lang, onboarding_done: true });
      updateUser(res.data);
    } catch { toast.error("Could not save profile"); }
    navigate(dest || "/dashboard");
  };

  const langs = [{ c: "en", n: "English" }, { c: "es", n: "Español" }, { c: "pt", n: "Português" }];

  return (
    <div className="min-h-screen hero-bg flex flex-col">
      <div className="px-6 py-4"><Logo /></div>
      <div className="flex flex-1 items-center justify-center px-5 pb-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg rounded-3xl bg-white border border-slate-100 card-shadow-lg p-8">
          <div className="flex gap-1.5 mb-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "brand-gradient" : "bg-slate-100"}`} />
            ))}
          </div>

          {step === 1 && (
            <div>
              <h1 className="font-display text-2xl font-semibold text-navy">{t("ob_lang")}</h1>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {langs.map((l) => (
                  <button key={l.c} data-testid={`ob-lang-${l.c}`} onClick={() => setLang(l.c)}
                    className={`rounded-2xl border py-4 text-sm font-semibold transition-all ${
                      lang === l.c ? "border-brandblue bg-blue-50 text-brandblue" : "border-slate-200 text-navy hover:bg-slate-50"
                    }`}>{l.n}</button>
                ))}
              </div>
              <Button data-testid="ob-next-1" onClick={() => setStep(2)} className="mt-7 w-full rounded-full brand-gradient text-white py-6">{t("ob_continue")}</Button>
            </div>
          )}

          {step === 2 && (
            <div>
              <h1 className="font-display text-2xl font-semibold text-navy">{t("ob_where")}</h1>
              <p className="text-sm text-slate-500 mt-1">{t("ob_state_rules")}</p>
              <div className="mt-5 space-y-4">
                <div>
                  <Label>{t("state")}</Label>
                  <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                    <SelectTrigger data-testid="ob-state" className="mt-1.5 rounded-xl"><SelectValue placeholder={t("ob_select_state")} /></SelectTrigger>
                    <SelectContent>
                      {states.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("zip")}</Label>
                  <Input data-testid="ob-zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="mt-1.5 rounded-xl" placeholder="94103" />
                </div>
                <div>
                  <Label>{t("household")}</Label>
                  <Select value={form.household_type} onValueChange={(v) => setForm({ ...form, household_type: v })}>
                    <SelectTrigger data-testid="ob-household" className="mt-1.5 rounded-xl"><SelectValue placeholder={t("ob_select")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">{t("hh_single")}</SelectItem>
                      <SelectItem value="married">{t("hh_married_partnered")}</SelectItem>
                      <SelectItem value="family">{t("hh_family")}</SelectItem>
                      <SelectItem value="retired">{t("hh_retired")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-7 flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-full flex-1">{t("ob_back")}</Button>
                <Button data-testid="ob-next-2" onClick={() => setStep(3)} className="rounded-full brand-gradient text-white flex-1">{t("ob_continue")}</Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h1 className="font-display text-2xl font-semibold text-navy">{t("ob_help")}</h1>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {goals.map((g) => (
                  <button key={g.labelKey} data-testid={`ob-goal-${g.labelKey}`} onClick={() => finish(g.to)}
                    className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4 text-left hover:border-brandblue hover:bg-blue-50/40 transition-colors">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl brand-gradient text-white"><g.icon className="h-5 w-5" /></span>
                    <span className="text-sm font-medium text-navy">{t(g.labelKey)}</span>
                  </button>
                ))}
              </div>
              <button data-testid="ob-skip" onClick={() => finish("/dashboard")} className="mt-5 w-full text-sm text-slate-400 hover:text-slate-600">{t("ob_skip")}</button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
